import { useQuery } from "@tanstack/react-query";
import { useCompany } from "@/contexts/CompanyContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

export type PermLevel = "none" | "view" | "edit" | "cancel";

const LEVEL_RANK: Record<PermLevel, number> = { none: 0, view: 1, edit: 2, cancel: 3 };

export function levelMeets(have: PermLevel, required: PermLevel): boolean {
  return LEVEL_RANK[have] >= LEVEL_RANK[required];
}

interface PermissionsMap {
  isOwner: boolean;
  isAdmin: boolean;
  /** Level granted by the base membership role alone (before custom roles). */
  base: PermLevel;
  levels: Record<string, PermLevel>;
}

/**
 * Effective level granted by the base membership role alone.
 * Mirrors public.can_edit_page in the database: only owner/admin get blanket
 * access. Every other membership role grants NOTHING on its own — the custom
 * role assigned by the company admin is the single source of permissions.
 */
export const BASE_ROLE_LEVEL: Record<string, PermLevel> = {
  owner: "cancel",
  admin: "cancel",
  accountant: "none",
  reviewer: "none",
  viewer: "none",
};

export function maxLevel(a: PermLevel, b: PermLevel): PermLevel {
  return LEVEL_RANK[a] >= LEVEL_RANK[b] ? a : b;
}

export function usePermissions() {
  const { user } = useAuth();
  const { currentCompany } = useCompany();
  const userId = user?.id;
  const companyId = currentCompany?.id;
  const role = currentCompany?.role;
  const isOwner = role === "owner";
  const isAdmin = role === "admin";
  // Owner/admin get blanket access on the client to match DB enforcement.
  const isPrivileged = isOwner || isAdmin;
  const base: PermLevel = BASE_ROLE_LEVEL[role ?? ""] ?? "none";

  return useQuery<PermissionsMap>({
    queryKey: ["permissions", userId, companyId, role],
    enabled: !!userId && !!companyId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (isPrivileged) return { isOwner, isAdmin, base, levels: {} };


      // Pull all role permissions for this user in this company in one call.
      const { data, error } = await supabase
        .from("company_member_roles")
        .select("role_permissions:role_id(role_permissions(page_key, level))")
        .eq("user_id", userId!)
        .eq("company_id", companyId!);

      if (error) throw error;

      // Fallback: query role_permissions joined manually if shape differs.
      const levels: Record<string, PermLevel> = {};

      // Defensive parse — supabase nested select shape can vary.
      const raw = (data ?? []) as any[];
      for (const row of raw) {
        const rp = row?.role_permissions?.role_permissions ?? row?.role_permissions ?? [];
        const list = Array.isArray(rp) ? rp : [rp];
        for (const p of list) {
          if (!p?.page_key || !p?.level) continue;
          const cur = levels[p.page_key] ?? "none";
          if (LEVEL_RANK[p.level as PermLevel] > LEVEL_RANK[cur]) levels[p.page_key] = p.level;
        }
      }

      // If nested join returned empty, do a second flat query.
      if (Object.keys(levels).length === 0) {
        const { data: roleRows } = await supabase
          .from("company_member_roles")
          .select("role_id")
          .eq("user_id", userId!)
          .eq("company_id", companyId!);
        const ids = (roleRows ?? []).map((r: any) => r.role_id);
        if (ids.length > 0) {
          const { data: perms } = await supabase
            .from("role_permissions")
            .select("page_key, level")
            .in("role_id", ids);
          for (const p of perms ?? []) {
            const lvl = (p as any).level as PermLevel;
            const key = (p as any).page_key as string;
            const cur = levels[key] ?? "none";
            if (LEVEL_RANK[lvl] > LEVEL_RANK[cur]) levels[key] = lvl;
          }
        }
      }

      return { isOwner, isAdmin, base, levels };
    },
  });
}

export function usePagePermission(pageKey: string | null) {
  const { data, isLoading } = usePermissions();
  const privileged = !!(data?.isOwner || data?.isAdmin);
  // Effective level = highest of the base membership role and the custom roles.
  const level: PermLevel = !pageKey
    ? "cancel"
    : privileged
    ? "cancel"
    : maxLevel(data?.base ?? "none", data?.levels[pageKey] ?? "none");

  return {
    isLoading,
    isOwner: !!data?.isOwner,
    isAdmin: !!data?.isAdmin,
    isPrivileged: privileged,
    level,
    canView: levelMeets(level, "view"),
    canEdit: levelMeets(level, "edit"),
    canCancel: levelMeets(level, "cancel"),
  };
}
